import type { BazelActions, BazelBuild } from "@/client/director/buildLogs.tsx";

// Shared Bazel build vocabulary for the console: statuses, where actions ran,
// and the remote cache rate. The Bazel page and the instance build logs both
// read builds through these, so their numbers and wording agree.

export function isTerminalStatus(status: string): boolean {
  const s = status.toUpperCase();
  return (
    s === "SUCCEEDED" ||
    s === "FAILED" ||
    s === "CANCELLED" ||
    s === "INCOMPLETE"
  );
}

export function hasStatus(
  status: string,
  want: "SUCCEEDED" | "FAILED" | "RUNNING",
): boolean {
  return status.toUpperCase() === want;
}

export function targetOf(build: Pick<BazelBuild, "pattern">): string {
  return build.pattern?.length ? build.pattern.join(" ") : "Bazel build";
}

// A casual semantic palette: cache hits green, remote execution blue, local
// amber, internal (uncacheable bookkeeping) muted, and an indigo accent.
export type BarTone = "hit" | "remote" | "local" | "internal" | "accent";
export const barToneClass: Record<BarTone, string> = {
  hit: "bg-emerald-500/70",
  remote: "bg-blue-500/70",
  local: "bg-amber-500/70",
  internal: "bg-slate-400/55",
  accent: "bg-indigo-400/65",
};

// runnerTone maps a Bazel runner name to a semantic color: cache hits are the win,
// remote/local are real execution, internal is in-process bookkeeping.
export function runnerTone(name: string): BarTone {
  if (name.includes("cache hit")) return "hit";
  if (name === "internal") return "internal";
  if (name === "remote") return "remote";
  return "local";
}

// CacheStats is a build's remote cache rate over cacheable actions: internal
// actions (symlink trees, file writes, manifests) run inside Bazel and can never
// be cached. With nothing cacheable, a build that succeeded was up to date; one
// that failed stopped before any remote work.
export type CacheStats = {
  hits: number;
  cacheable: number;
  ratePct: number;
  kind: "rate" | "upToDate" | "noRemoteWork";
};

export function cacheStats(a: BazelActions, status: string): CacheStats {
  const internal = a.runners?.find((r) => r.name === "internal")?.count ?? 0;
  const cacheable = a.total - internal;
  return {
    hits: a.cacheHits,
    cacheable,
    ratePct: cacheable > 0 ? Math.round((a.cacheHits / cacheable) * 100) : 0,
    kind:
      cacheable > 0
        ? "rate"
        : hasStatus(status, "SUCCEEDED")
          ? "upToDate"
          : "noRemoteWork",
  };
}

export function cacheLabel(c: CacheStats): string {
  return c.kind === "rate"
    ? `${c.ratePct}% from cache`
    : c.kind === "upToDate"
      ? "up to date"
      : "no remote work";
}
